import type { TelemetryClient } from "./client.js";

export function attachClientErrorListeners(client: TelemetryClient): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const onError = (event: ErrorEvent) => {
    const target = event.target;
    if (target && target !== window && (target as HTMLElement).tagName) {
      client.captureResourceError(event);
      return;
    }
    client.captureError(event.error ?? event.message, {
      framework: "browser",
      handled: false,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        source: "window.error",
      },
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    client.captureUnhandledRejection(event.reason, {
      metadata: { source: "window.unhandledrejection" },
    });
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

export function attachServerErrorListeners(client: TelemetryClient): () => void {
  if (typeof process === "undefined" || !process.on) {
    return () => undefined;
  }

  const onUncaught = (error: Error) => {
    client.captureError(error, {
      handled: false,
      metadata: { source: "process.uncaughtException" },
    });
  };

  const onRejection = (reason: unknown) => {
    client.captureUnhandledRejection(reason, {
      metadata: { source: "process.unhandledRejection" },
    });
  };

  process.on("uncaughtException", onUncaught);
  process.on("unhandledRejection", onRejection);

  return () => {
    process.off("uncaughtException", onUncaught);
    process.off("unhandledRejection", onRejection);
  };
}

